import { ForwardedRef, forwardRef } from 'react';
import cn from 'classnames';
import { MdError } from 'react-icons/md';
import { ScheduleViolationTooltip } from '@lp/features/Scheduler/components';
import { errorColumnHeaderClassname } from '@lp/features/Scheduler/constants';
import { SchedulerDayColumn, ServiceViolationItem } from '@lp/features/Scheduler/types';
import { getColumnHeaderClassname } from '@lp/features/Scheduler/utils';
import { NameByValue } from '@/types';
import { DayColumnHeaderFilterDropdown } from './components';

type DayHeaderProps<S extends string> = {
  column: SchedulerDayColumn;
  withFilter?: boolean;
  violations?: ServiceViolationItem | ServiceViolationItem[];
  filterOptionList: NameByValue<S>[];
  selectedKeys?: Set<S>;
  onSelectedKeysChange?: (selectedKeys: Set<S>) => void;
};

const DayColumnHeaderComponent = <S extends string>(
  { column, withFilter, violations, filterOptionList, selectedKeys, onSelectedKeysChange }: DayHeaderProps<S>,
  ref: ForwardedRef<HTMLTableCellElement>,
) => {
  const classname = violations ? errorColumnHeaderClassname : getColumnHeaderClassname(column);

  return (
    <th ref={ref} className={cn('min-w-[158px]', classname)}>
      <div className={cn('flex items-center justify-between gap-2', { 'opacity-60': !column.isCurrentMonth })}>
        <div className="flex items-center gap-1">
          <span>{column.label}</span>
          {withFilter && (
            <DayColumnHeaderFilterDropdown
              title={column.label}
              optionList={filterOptionList}
              selectedKeys={selectedKeys}
              onSelectedKeysChange={onSelectedKeysChange}
              active={column.isToday}
            />
          )}
        </div>
        {violations && (
          <ScheduleViolationTooltip items={violations} placement="bottom">
            <div>
              <MdError className="text-danger-30" size={16} />
            </div>
          </ScheduleViolationTooltip>
        )}
      </div>
    </th>
  );
};

type DayColumnHeaderType = <S extends string>(
  props: DayHeaderProps<S> & { ref?: ForwardedRef<HTMLTableCellElement> },
) => ReturnType<typeof DayColumnHeaderComponent>;

export const DayColumnHeader = forwardRef(DayColumnHeaderComponent) as DayColumnHeaderType;
